import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDirection } from '@/shared/hooks/useDirection/useDirection';
import { formatNumber } from '@/shared/utils/format-number/format-number.util';

import {
  HEIGHT_UNITS,
  MAX_HEIGHT_CM,
  MIN_HEIGHT_CM,
  fromFeetInches,
  toFeetInches,
  type HeightUnit,
} from '../height-scale/height-scale.util';
import { useScalarAnnouncer } from '../useScalarAnnouncer/useScalarAnnouncer';
import { useScalarNumber, type UseScalarNumber } from '../useScalarNumber/useScalarNumber';

import { type HeightInputProps } from './HeightInput.types';

export type UseHeightInputLogicInput = Omit<
  HeightInputProps,
  'id' | 'className' | 'size' | 'aria-describedby'
>;

export interface UseHeightInputLogic {
  /** The centimetre box. Always live — it is what the value IS. */
  cm: UseScalarNumber;
  feet: UseScalarNumber;
  inches: UseScalarNumber;
  /** The unit on screen. Never what the value is stored in — that is always cm. */
  unit: HeightUnit;
  units: readonly HeightUnit[];
  onUnitChange: (unit: HeightUnit) => void;
  unitName: (unit: HeightUnit) => string;
  /** The bounds actually in force, in centimetres — clamped, never the raw props. */
  minCm: number;
  maxCm: number;
  /** What the platform will actually store, said in both units. */
  storedHint: string;
  /** The legal range, in the unit on screen. Never absent — the platform has one. */
  rangeHint: string;
  /** Why the amount on screen cannot be stored, when it cannot. */
  warning: string | undefined;
  announcement: string;
  amountLabel: string;
  feetLabel: string;
  inchesLabel: string;
  unitLabel: string;
}

/**
 * A member's height: stored as one integer centimetre, edited in whichever unit
 * the reader thinks in.
 *
 * `height` is deliberately **not** a `number` question — `question-type.ts` says
 * why, and this control is the other half of that decision. Two things it does
 * that a number box cannot:
 *
 * **The stored centimetre is the authority, and switching units writes
 * nothing.** An inch is 2.54cm, so cm → ft/in → cm is lossy by up to half an
 * inch; a control that converted on every switch would walk a member's height
 * down the page. The imperial pair is *derived* on every render and only an
 * actual edit commits, which is `DurationInput`'s invariant applied to a scale
 * that is not a clean ratio.
 *
 * **The bounds are clamped into the platform's own**, exactly as
 * `answerValueSchema`'s `heightSchema` clamps them, so no staff configuration
 * can draw a range whose ends the api would refuse.
 */
export function useHeightInputLogic({
  label,
  value,
  onValueChange,
  min,
  max,
  isReadOnly = false,
  isDisabled = false,
}: UseHeightInputLogicInput): UseHeightInputLogic {
  const { t } = useTranslation('uiKit');
  const { locale } = useDirection();
  const announcer = useScalarAnnouncer();

  const [unit, setUnit] = useState<HeightUnit>('cm');

  const lowCm = Math.max(min ?? MIN_HEIGHT_CM, MIN_HEIGHT_CM);
  const highCm = Math.min(max ?? MAX_HEIGHT_CM, MAX_HEIGHT_CM);

  // `null` and `undefined` are the same thing to a control that has no third
  // state: there is no height here. Only `undefined` is ever emitted.
  const stored = value ?? undefined;
  const pair = stored === undefined ? undefined : toFeetInches(stored);

  const refuse = useCallback((): boolean => {
    if (isDisabled) return true;
    if (!isReadOnly) return false;
    announcer.announceReadOnlyRefusal();
    return true;
  }, [announcer, isDisabled, isReadOnly]);

  const commitCm = useCallback(
    (next: number | undefined) => {
      onValueChange(next);
    },
    [onValueChange],
  );

  /**
   * Either imperial box, cleared, clears the whole height.
   *
   * Reading a cleared box as "zero of that component" would make the field
   * impossible to empty: clearing feet would store 8in, whose display is
   * `0ft 8in`, and the pair would never both be absent again.
   */
  const commitFeet = useCallback(
    (next: number | undefined) => {
      onValueChange(next === undefined ? undefined : fromFeetInches(next, pair?.inches));
    },
    [onValueChange, pair?.inches],
  );

  const commitInches = useCallback(
    (next: number | undefined) => {
      onValueChange(next === undefined ? undefined : fromFeetInches(pair?.feet, next));
    },
    [onValueChange, pair?.feet],
  );

  const cm = useScalarNumber({
    displayValue: stored,
    step: 1,
    isIntegerOnly: true,
    min: lowCm,
    max: highCm,
    isReadOnly,
    isDisabled,
    onCommit: commitCm,
    onRefuse: announcer.announceReadOnlyRefusal,
  });

  const feet = useScalarNumber({
    displayValue: pair?.feet,
    step: 1,
    isIntegerOnly: true,
    min: toFeetInches(lowCm).feet,
    max: toFeetInches(highCm).feet,
    isReadOnly,
    isDisabled,
    onCommit: commitFeet,
    onRefuse: announcer.announceReadOnlyRefusal,
  });

  const inches = useScalarNumber({
    displayValue: pair?.inches,
    step: 1,
    isIntegerOnly: true,
    // The stepper wraps nothing: 11 + 1 clamps rather than carrying into feet,
    // because an arrow that silently changes the OTHER box is a surprise. Typing
    // 13 still normalises — that is an intention, not a slip.
    min: 0,
    max: 11,
    isReadOnly,
    isDisabled,
    onCommit: commitInches,
    onRefuse: announcer.announceReadOnlyRefusal,
  });

  const unitName = useCallback(
    (forUnit: HeightUnit): string => t(`height.unitName.${forUnit}`),
    [t],
  );

  const onUnitChange = useCallback(
    (next: HeightUnit) => {
      // Read-only refuses this too: it changes what is on screen, and a control
      // that says "read-only" while one of its parts still moves is worse than
      // one that says nothing.
      if (refuse()) return;

      cm.reset();
      feet.reset();
      inches.reset();
      setUnit(next);
      announcer.say(t('height.switchedToUnit', { unit: unitName(next) }));
    },
    [announcer, cm, feet, inches, refuse, t, unitName],
  );

  const cmText = useCallback(
    (centimetres: number): string =>
      t('height.cmValue', { value: formatNumber(centimetres, locale) }),
    [locale, t],
  );

  const ftInText = useCallback(
    (centimetres: number): string => {
      const { feet: ft, inches: inch } = toFeetInches(centimetres);
      return t('height.ftInValue', {
        feet: formatNumber(ft, locale),
        inches: formatNumber(inch, locale),
      });
    },
    [locale, t],
  );

  const storedHint = useMemo(() => {
    if (stored === undefined) return t('scalar.empty');

    // Centimetres first whichever unit is on screen: the hint reports what is
    // STORED, and an operator reading "5 ft 8 in" as the stored value would be
    // reading a rendering of it.
    return t('height.stored', { value: cmText(stored), alt: ftInText(stored) });
  }, [cmText, ftInText, stored, t]);

  const rangeHint = useMemo(() => {
    const show = unit === 'cm' ? cmText : ftInText;
    return t('height.range', { min: show(lowCm), max: show(highCm) });
  }, [cmText, ftInText, highCm, lowCm, t, unit]);

  const isDraftInvalid =
    unit === 'cm' ? cm.isDraftInvalid : feet.isDraftInvalid || inches.isDraftInvalid;

  return useMemo(
    () => ({
      cm,
      feet,
      inches,
      unit,
      units: HEIGHT_UNITS,
      onUnitChange,
      unitName,
      minCm: lowCm,
      maxCm: highCm,
      storedHint,
      rangeHint,
      warning: isDraftInvalid ? t('height.wholeOnly') : undefined,
      announcement: announcer.announcement,
      amountLabel: t('height.amountFor', { label }),
      feetLabel: t('height.feetFor', { label }),
      inchesLabel: t('height.inchesFor', { label }),
      unitLabel: t('height.unitFor', { label }),
    }),
    [
      announcer.announcement,
      cm,
      feet,
      highCm,
      inches,
      isDraftInvalid,
      label,
      lowCm,
      onUnitChange,
      rangeHint,
      storedHint,
      t,
      unit,
      unitName,
    ],
  );
}
